import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Users } from 'lucide-react';
import { AudienceGated, Itinerary, ItineraryItem } from '../types';
import { getSharedItinerary, getPublicItineraryBySlug } from '../services';
import { forkItinerary } from '../services/plannerApi';
import { useUserStore } from '../stores';
import { trackEvent } from '../hooks/useAnalytics';
import { storeForkIntent, storeSharedForkIntent } from '../utils/forkIntent';
import ForkModal from '../components/plans/ForkModal';
import UnifiedMap from '../components/UnifiedMap';
import { itineraryItemsToMarkers } from '../utils/mapMarkers';
import { UnsplashCredit } from '../components/common/UnsplashCredit';
import { useOptionalTheme } from '../contexts/ThemeContext';
import { sunsetTagFor, type TagPlan } from '../utils/sunsetTag';
import SunsetTag from '../components/itinerary/SunsetTag';
import '../components/plans/PlanDetail.css';
import './SharedItinerary.css';

interface GroupedItems {
  [date: string]: ItineraryItem[];
}

/**
 * Which itinerary the page shows, and by what authority.
 *
 * - `token`: the share link (`/shared/<token>`). The token opens a private or
 *   link plan and is the fork's authority.
 * - `slug`: the public page (`/plans/p/<slug>`) when no published
 *   CommunityPlan holds the slug. Public plans only; it forks by id.
 *
 * Both kinds render the same page; only the fetch, the fork, the anonymous
 * fork intent and the analytics `source` differ.
 */
export type ItinerarySource =
  | { kind: 'token'; token: string }
  | { kind: 'slug'; slug: string };

interface SharedItineraryProps {
  /** Defaults to the route's `:token`. */
  source?: ItinerarySource;
}

const SharedItinerary: React.FC<SharedItineraryProps> = ({ source }) => {
  const { token: routeToken } = useParams<{ token: string }>();
  const kind: ItinerarySource['kind'] = source?.kind ?? 'token';
  const token = source ? (source.kind === 'token' ? source.token : undefined) : routeToken;
  const slug = source?.kind === 'slug' ? source.slug : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore((s) => s.user);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [showForkModal, setShowForkModal] = useState(false);
  // Activities whose own photograph failed to load. The row goes back to text
  // rather than keeping a broken frame on a page a stranger is reading.
  const [failedPhotos, setFailedPhotos] = useState<Set<number>>(() => new Set());
  // The SUNSET tag (Almanac S3, Decision 6), exactly as the itinerary rows
  // carry it: the viewer's own preference (default on) and the plan's
  // serialized `effective_timezone` as the stop clock.
  const tagSunsetStops = useOptionalTheme()?.tagSunsetStops ?? true;
  // Who sent the link, when the link says. The sharer's first name only —
  // that is all the share endpoints put in `?from=`.
  const sharedBy = useMemo(
    () => new URLSearchParams(location.search).get('from') || null,
    [location.search],
  );

  // Keyed on the source's identity — its kind and its token or slug — so a
  // new source refetches (a slug page has no token; keyed on the token alone,
  // a slug change would keep the previous plan).
  useEffect(() => {
    const fetcher = kind === 'slug'
      ? (slug ? () => getPublicItineraryBySlug(slug) : null)
      : (token ? () => getSharedItinerary(token) : null);
    if (!fetcher) return;
    // StrictMode mounts, unmounts and mounts again in development, which
    // starts two fetches; without this guard the first response would land
    // as a second, distinct itinerary object and fire the visit twice.
    let live = true;
    setItinerary(null);
    setError('');
    setIsLoading(true);
    loadItinerary(fetcher, () => live);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, token, slug]);

  // Funnel: the receiving side of share→fork. `from` set marks a visit that
  // came from a share link; `itinerary_id` is what `plan_shared` carries, so
  // the two join. The share token is never passed here — and posthog-js's
  // own URL properties are redacted at the SDK boundary (useAnalytics).
  // Once per loaded itinerary: the load effect's cleanup is what makes that
  // true under StrictMode's double mount (only the surviving fetch lands).
  // On the public page the slug is a public fact about the plan and joins
  // like the community page's; `source` keeps the two joins apart.
  useEffect(() => {
    if (!itinerary) return;
    if (kind === 'slug') {
      trackEvent('plan_page_visit', {
        itinerary_id: itinerary.id,
        slug,
        source: 'public_page',
        referrer: document.referrer || '',
        from: sharedBy || undefined,
      });
      return;
    }
    trackEvent('plan_page_visit', {
      itinerary_id: itinerary.id,
      source: 'share_link',
      referrer: document.referrer || '',
      from: sharedBy || undefined,
    });
    // Once per loaded itinerary, not once per `from` re-parse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itinerary]);

  // Resume a fork interrupted by signup: useUserStore sends the new account
  // back here with ?fork=1, so reopen the modal instead of making them find
  // the button again. The flag is consumed so a reload does not re-open it.
  useEffect(() => {
    if (!itinerary || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('fork') === '1') {
      params.delete('fork');
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
      setShowForkModal(true);
    }
  }, [itinerary, user, location.pathname, location.search, navigate]);

  const handleFork = () => {
    if (!itinerary) return;
    if (!user) {
      // The stored intent is the way back (useUserStore resumes it after
      // signup); no `returnTo`, because that would copy the token into a
      // second URL — one that is logged, bookmarked and analytics-captured.
      if (kind === 'slug') {
        if (slug) storeForkIntent(slug);
      } else if (token) {
        storeSharedForkIntent(token);
      }
      navigate('/login');
      return;
    }
    setShowForkModal(true);
  };

  const handleForkConfirm = async (startDate: string): Promise<{ id: number }> => {
    if (!itinerary) throw new Error('No plan');
    // A public plan forks by id (the public-by-id path); only a token page
    // passes the token, its authority for a private or link plan.
    const result = kind === 'slug'
      ? await forkItinerary(itinerary.id, startDate)
      : await forkItinerary(itinerary.id, startDate, token);
    // Funnel: visit→fork completes. Keyed by the SOURCE itinerary (what the
    // visit carried), never the copy just made — and, again, no token.
    trackEvent('plan_forked', {
      itinerary_id: itinerary.id,
      destination: itinerary.destination || '',
      source: kind === 'slug' ? 'public_page' : 'share_link',
    });
    return { id: result.id };
  };

  const loadItinerary = async (fetcher: () => Promise<Itinerary>, isLive: () => boolean = () => true) => {
    try {
      const data = await fetcher();
      if (isLive()) setItinerary(data);
    } catch (err) {
      if (isLive()) setError(err instanceof Error ? err.message : 'Failed to load itinerary');
    } finally {
      if (isLive()) setIsLoading(false);
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const groupItemsByDate = useCallback(() => {
    if (!itinerary) return {};
    const grouped: GroupedItems = {};
    itinerary.items.forEach(item => {
      const date = item.date || itinerary.start_date || 'unscheduled';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(item);
    });
    const sorted: GroupedItems = {};
    Object.keys(grouped).sort().forEach(date => { sorted[date] = grouped[date]; });
    return sorted;
  }, [itinerary]);

  const grouped = useMemo(() => groupItemsByDate(), [groupItemsByDate]);
  const sunsetClock: TagPlan | undefined = itinerary
    ? { effective_timezone: itinerary.effective_timezone }
    : undefined;
  const isSunsetRow = (item: ItineraryItem) =>
    tagSunsetStops && !!sunsetClock && sunsetTagFor(item, sunsetClock);
  const dayKeys = useMemo(() => Object.keys(grouped), [grouped]);

  const durationDays = itinerary?.trip_duration_days
    ?? (itinerary?.start_date && itinerary?.end_date
      ? Math.round((new Date(itinerary.end_date + 'T00:00:00').getTime() - new Date(itinerary.start_date + 'T00:00:00').getTime()) / 86400000) + 1
      : null);

  // Loading state
  if (isLoading) {
    return (
      <div className="shared-page">
        <div className="shared-loading">
          <div className="shared-logo">VizDay</div>
          <div className="shared-loading-skeleton">
            <div className="shared-loading-bar wide" />
            <div className="shared-loading-bar medium" />
          </div>
          <p className="shared-loading-text">Loading itinerary...</p>
        </div>
      </div>
    );
  }

  // Error / not found. The public page answers in the community page's
  // words: a visitor reached `/plans/p/<slug>`, not a link someone sent.
  if ((error || !itinerary) && kind === 'slug') {
    return (
      <div className="shared-page">
        <div className="shared-error">
          <div className="shared-logo">VizDay</div>
          <h2 className="shared-error-title">Plan not found</h2>
          <p className="shared-error-message">This plan may have been removed or the link is incorrect.</p>
          <Link to="/explore" className="shared-error-btn">Back to Explore</Link>
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !itinerary) {
    return (
      <div className="shared-page">
        <div className="shared-error">
          <div className="shared-logo">VizDay</div>
          <div className="shared-error-icon">🔗</div>
          <h2 className="shared-error-title">{error ? 'This link has expired' : 'Itinerary not found'}</h2>
          <p className="shared-error-message">This link may have expired or been removed.</p>
          <Link to="/plans" className="shared-error-btn">Explore Plans</Link>
        </div>
      </div>
    );
  }

  // Whether the server that answered withholds photographs nobody consented
  // to — see the activity rows below for why the page asks.
  const audienceGated = (itinerary as Itinerary & AudienceGated).audience_gated === true;

  return (
    <div className="plan-detail shared-plan">
      {/* Hero — reuses plan-detail-hero tokens */}
      <div
        className={itinerary.hero_image_url
          ? 'plan-detail-hero'
          : 'plan-detail-hero plan-detail-hero--bare'}
        style={itinerary.hero_image_url
          ? { background: `url(${itinerary.hero_image_url}) center/cover` }
          : undefined
        }
      >
        {/* The hero is the first thing a shared link shows, and it was the
            one Unsplash surface crediting nobody. It renders only when the
            server says this photograph is theirs — a plan's own upload
            resolves to null attribution, and crediting Unsplash for it would
            attribute someone's personal photo to a stock library. */}
        {itinerary.hero_attribution && (
          <UnsplashCredit
            photographer={itinerary.hero_attribution.photographer}
            profileLink={itinerary.hero_attribution.unsplash_link}
            className="shared-hero-credit"
          />
        )}
        <div className="plan-detail-hero-overlay">
          <button className="plan-detail-back-btn" onClick={() => navigate('/plans')}>
            <ArrowLeft size={20} />
          </button>
          <Link to="/" className="shared-hero-logo">VizDay</Link>
          <h1 className="plan-detail-title">{itinerary.title}</h1>
          <div className="plan-detail-meta-row">
            {itinerary.destination && (
              <span><MapPin size={14} /> {itinerary.destination}</span>
            )}
            {durationDays && (
              <span><Calendar size={14} /> {durationDays} day{durationDays !== 1 ? 's' : ''}</span>
            )}
            {itinerary.group_type && (
              <span><Users size={14} /> {itinerary.group_type}</span>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="plan-detail-stats">
        <div className="plan-detail-stat">
          <span className="plan-detail-stat-num">{itinerary.items.length}</span>
          <span className="plan-detail-stat-label">activities</span>
        </div>
        {durationDays && (
          <div className="plan-detail-stat">
            <span className="plan-detail-stat-num">{durationDays}</span>
            <span className="plan-detail-stat-label">days</span>
          </div>
        )}
        {itinerary.season && (
          <div className="plan-detail-stat">
            <span className="plan-detail-stat-num">{itinerary.season}</span>
            <span className="plan-detail-stat-label">season</span>
          </div>
        )}
      </div>

      {/* Itinerary section */}
      <div className="plan-detail-section">
        {itinerary.description && (
          <p className="shared-description">{itinerary.description}</p>
        )}

        <h2 className="plan-detail-section-title">Itinerary</h2>

        {/* Map */}
        {(() => {
          const markers = itineraryItemsToMarkers(
            selectedDay ? (grouped[selectedDay] || []) : itinerary.items
          );
          return markers.length > 0 ? (
            <div className="shared-map-wrap">
              <UnifiedMap markers={markers} />
            </div>
          ) : null;
        })()}

        {/* Day pills */}
        {dayKeys.length > 1 && (
          <div className="plan-detail-day-pills">
            <button
              className={`plan-detail-day-pill ${selectedDay === null ? 'active' : ''}`}
              onClick={() => setSelectedDay(null)}
            >
              Overview
            </button>
            {dayKeys.map((date, i) => (
              <button
                key={date}
                className={`plan-detail-day-pill ${selectedDay === date ? 'active' : ''}`}
                onClick={() => setSelectedDay(date)}
              >
                Day {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* Day-by-day */}
        {dayKeys
          .filter(date => selectedDay === null || date === selectedDay)
          .map(date => {
            const items = grouped[date];
            const dayNum = dayKeys.indexOf(date) + 1;
            const dayDate = new Date(date + 'T00:00:00');
            const dayLabel = dayDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

            // The day's own photograph, when it resolved to one. The day tier
            // falls back to the destination photo when an activity subject
            // finds nothing, so this is populated for nearly every day — which
            // is why it is a per-DAY treatment and not a per-activity one.
            // Per activity, Unsplash has nothing for "Caldera Trail Hike" and
            // the page would read as one that failed to load.
            const dayPhoto = itinerary.day_images?.[date];

            return (
              <div key={date} className="plan-detail-day">
                {dayPhoto?.url ? (
                  <>
                    <div
                      className="shared-day-band"
                      style={{ backgroundImage: `url(${dayPhoto.url})` }}
                    >
                      <h3 className="shared-day-band-label">Day {dayNum}: {dayLabel}</h3>
                    </div>
                    {dayPhoto.photographer && (
                      // Both licences require the photographer be named. In the
                      // app that is a tap-through on the thumbnail; a page a
                      // crawler reads needs it visible.
                      <p className="shared-day-credit">
                        <UnsplashCredit
                          photographer={dayPhoto.photographer}
                          profileLink={dayPhoto.unsplash_link}
                        />
                      </p>
                    )}
                  </>
                ) : (
                  <h3 className="plan-detail-day-label">Day {dayNum}: {dayLabel}</h3>
                )}
                <ul className="plan-detail-activities">
                  {items.map((item: ItineraryItem, i: number) => {
                    // The traveller's OWN photograph, and only that rung. The
                    // server sends it to a link-holder only once its owner was
                    // told link-holders would see it — at upload, or by adding
                    // an older one to the link — so drawing it here is what
                    // that consent was for. It needs no credit: it is theirs.
                    // Stock and venue photographs stay off the activity rows
                    // for the reason the day bands note above.
                    // Only from a server that applied the rule. During a deploy an
                    // old container can answer this page and sends every
                    // photograph ungated; it does not send `audience_gated`,
                    // so from it the page draws none, exactly as before.
                    const ownPhoto = audienceGated ? item.image?.user_photo_url : undefined;
                    const showPhoto = Boolean(ownPhoto) && !failedPhotos.has(item.id);
                    return (
                      <li
                        key={item.id}
                        className={`plan-detail-activity${showPhoto ? ' shared-activity--with-photo' : ''}`}
                      >
                        <span className="plan-detail-activity-num">{i + 1}</span>
                        {item.time && <span className="plan-detail-time">{formatTime(item.time)}</span>}
                        {/* Name and tag share one wrapping box that takes the
                            row's remaining width, so on a narrow row with a
                            photograph the tag drops beneath the name rather
                            than squeezing it (r913 f1). The name itself may
                            shrink and break a long word, or one unbroken place
                            name would still push past the photograph (r914 f1). */}
                        <span className="shared-activity-namebox">
                          <span className="shared-activity-namebox-text">{item.name}</span>
                          {isSunsetRow(item) && <SunsetTag />}
                        </span>
                        {showPhoto && (
                          // `alt=""`: the activity's name is already read out
                          // beside it, and repeating it says nothing new.
                          <img
                            className="shared-activity-photo"
                            src={ownPhoto}
                            alt=""
                            loading="lazy"
                            onError={() => setFailedPhotos((prev) => new Set(prev).add(item.id))}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
      </div>

      {/* Branding + CTA */}
      <div className="shared-branding">
        <Link to="/" className="shared-branding-logo">VizDay</Link>
        <p className="shared-branding-tagline">
          {sharedBy ? `Shared by ${sharedBy}` : 'AI-powered family travel planning'}
        </p>
      </div>

      {/* The loop this page exists to close: a shared plan becomes the
          recipient's own. Before this there was only a link to Explore, and a
          recipient who wanted the plan could read it and then leave. */}
      <div className="plan-detail-actions">
        <button type="button" className="plan-detail-fork-btn" onClick={handleFork}>
          Make it mine
        </button>
        <Link to="/plans" className="shared-cta-link">
          Explore Plans on VizDay
        </Link>
      </div>

      {showForkModal && (
        <ForkModal
          planTitle={itinerary.title}
          destination={itinerary.destination || ''}
          creatorName={itinerary.creator_name || sharedBy || 'VizDay'}
          durationDays={durationDays || 1}
          onConfirm={handleForkConfirm}
          onGoToPlan={(id) => navigate(`/itinerary/${id}?chat=1`)}
          onSharePlan={(id) => navigate(`/itinerary/${id}`)}
          onClose={() => setShowForkModal(false)}
        />
      )}
    </div>
  );
};

export default SharedItinerary;
